import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import CompetitorIntel from '../components/CompetitorIntel';
import ClientGuidelines, { Guidelines } from '../components/ClientGuidelines';
import { Calendar, BookOpen, Newspaper, ChevronLeft, ChevronRight, FileText, Video, Clock, CheckCircle2, Eye, Sparkles,  BarChart3, Smartphone, ArrowUpRight, Megaphone, ExternalLink, FolderOpen, Loader2, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { generateCalendarDays, createDateKey } from '../components/CalendarUtils';
import PublicLayout from '../components/PublicLayout';
import ClientContentEditor from '../components/ClientContentEditor';
import ClientNewContentForm, { ContenutoCreato } from '../components/ClientNewContentForm';
import ClientApprovazioni, { ContenutoApprovazione } from '../components/ClientApprovazioni';
import { getCookie } from '../contexts/AuthContext';

// La sezione "News & Competitor" compare da sé quando il cliente ha delle fonti
// censite nella sua scheda (tab News & Competitor): il backend lo dice con
// `intel_enabled` nel payload della pagina pubblica.

/** Elemento del Drive del cliente (cartella o file), come lo restituisce il backend. */
interface DriveItem {
  id: string;
  name: string;
  mimeType: string;
  isFolder: boolean;
  webViewLink: string | null;
  thumbnailLink: string | null;
  modifiedTime: string | null;
  size: number | null;
}

// Extended content type for public calendar (different from standard Contenuto)
interface PublicContenuto {
  id: string;
  formatoPost?: 'grafica' | 'foto';
  titolo: string;
  descrizione?: string;
  body?: string;
  hook?: string;
  cta?: string;
  stato: string; // Backend status strings: 'draft', 'text_ready', 'recorded', 'edited', 'scheduled', 'published', 'archived'
  dataPubblicazione?: string | Date;
  dataCreazione?: string;
  dataAggiornamento?: string;
  urlScriptFile?: string;
  urlVideoGrezzo?: string;
  urlVideoMontato?: string;
  urlThumbnail?: string;
  linkPubblicazione?: string;
  datiAnalitici?: unknown;
  note?: string;
  copyPost?: string;
  linkIspirazione?: string;
  copertinaUrl?: string;
  /** post = post grafico (solo copy + immagine), reel = video */
  tipo?: 'reel' | 'post';
  isPublished?: boolean;
  /** true = il videomaker ha confermato di averlo girato */
  videoRegistrato?: boolean;
  /** quando il videomaker l'ha segnato come girato */
  dataRegistrazione?: string;
}

/*
  Le due schede dei servizi ByMarke (Report KPI e Web Lead App). Stavano in
  fondo al calendario; ora sono sezioni proprie, raggiungibili dai pulsanti in
  alto come "Drive Clip" e "News & Competitor".
*/
const CardReportKpi: React.FC = () => (
    <article
      className="relative flex flex-col p-6 sm:p-7 rounded-2xl transition-all duration-300 hover:-translate-y-0.5 group overflow-hidden"
      style={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #EBEDF1',
      }}
    >
      {/* Glow ambient sottile in alto */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-32 opacity-60"
        style={{
          background:
            'radial-gradient(60% 100% at 50% 0%, rgba(19,22,28,0.03) 0%, transparent 100%)',
        }}
        aria-hidden="true"
      />

      <div className="relative flex flex-col flex-1">
        <div
          className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl inline-flex items-center justify-center mb-5"
          style={{
            backgroundColor: '#F4F5F7',
            border: '1px solid #DDE0E6',
          }}
        >
          <BarChart3 className="w-5 h-5 sm:w-[22px] sm:h-[22px]" style={{ color: '#13161C' }} />
        </div>

        <h3
          className="text-xl sm:text-[22px] font-medium mb-3"
          style={{
            color: '#13161C',
            letterSpacing: '-0.2px',
            fontFamily: "'Libre Baskerville', Georgia, serif",
            fontWeight: 400,
          }}
        >
          Report KPI &amp; Performance
        </h3>

        <p
          className="text-sm sm:text-[15px] leading-relaxed mb-4"
          style={{ color: '#5B6270' }}
        >
          Richiedi un report dettagliato realizzato dal team ByMarke su
          visibilità, interazioni, commenti ed engagement dei tuoi
          contenuti pubblicati.
        </p>

        {/* Nota informativa ADS */}
        <div
          className="flex items-start gap-2.5 p-3 rounded-lg mb-6"
          style={{
            backgroundColor: '#F4F5F7',
            border: '1px solid #EBEDF1',
          }}
        >
          <Megaphone
            className="w-4 h-4 mt-0.5 flex-shrink-0"
            style={{ color: '#5B6270' }}
          />
          <p
            className="text-xs sm:text-[13px] leading-snug"
            style={{ color: '#5B6270' }}
          >
            Per report più completi e strutturati si consiglia di avere
            attive campagne ADS pubblicitarie.
          </p>
        </div>

        {/* "Finto" bottone — solo visivo, nessun link */}
        <div
          className="mt-auto inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-md text-sm font-semibold select-none"
          style={{
            backgroundColor: '#FDF751',
            color: '#13161C',
            boxShadow: 'none',
          }}
          aria-hidden="true"
        >
          <span style={{ color: 'inherit' }}>Chiedi al Team ByMarke</span>
        </div>
      </div>
    </article>
);

const CardWebLeadApp: React.FC<{ link?: string | null }> = ({ link }) => (
    <article
      className="relative flex flex-col p-6 sm:p-7 rounded-2xl transition-all duration-300 hover:-translate-y-0.5 group overflow-hidden"
      style={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #EBEDF1',
      }}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-32 opacity-60"
        style={{
          background:
            'radial-gradient(60% 100% at 50% 0%, rgba(19,22,28,0.03) 0%, transparent 100%)',
        }}
        aria-hidden="true"
      />

      <div className="relative flex flex-col flex-1">
        <div
          className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl inline-flex items-center justify-center mb-5"
          style={{
            backgroundColor: '#F4F5F7',
            border: '1px solid #DDE0E6',
          }}
        >
          <Smartphone className="w-5 h-5 sm:w-[22px] sm:h-[22px]" style={{ color: '#13161C' }} />
        </div>

        <h3
          className="text-xl sm:text-[22px] font-medium mb-3"
          style={{
            color: '#13161C',
            letterSpacing: '-0.2px',
            fontFamily: "'Libre Baskerville', Georgia, serif",
            fontWeight: 400,
          }}
        >
          Web Lead App
        </h3>

        <p
          className="text-sm sm:text-[15px] leading-relaxed mb-6"
          style={{ color: '#5B6270' }}
        >
          Una web app integrata con le piattaforme social e il tuo sito,
          progettata per ricevere e gestire i lead direttamente da
          smartphone — semplice, veloce e sempre accessibile.
        </p>

        {/* Spacer per allineare i bottoni quando le card hanno testo diverso */}
        <div className="mt-auto">
          {link ? (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-md text-sm font-semibold transition-all duration-200 active:scale-[0.98] hover:-translate-y-px"
              style={{
                backgroundColor: '#FDF751',
                color: '#13161C',
                boxShadow: 'none',
              }}
            >
              <span style={{ color: 'inherit' }}>Apri Web Lead App</span>
              <ExternalLink className="w-4 h-4" style={{ color: 'inherit' }} />
            </a>
          ) : (
            <div
              className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-md text-sm font-semibold select-none"
              style={{
                backgroundColor: '#FDF751',
                color: '#13161C',
                boxShadow: 'none',
              }}
              aria-hidden="true"
            >
              <span style={{ color: 'inherit' }}>Chiedi al Team ByMarke</span>
            </div>
          )}
        </div>
      </div>
    </article>
);

/**
 * Reel già girato dal videomaker ma non ancora programmato: non va più in
 * "Da registrare". Lo stato può essere ancora 'text_ready' o 'draft': quello
 * che conta è il flag.
 */
const èRegistratoDaProgrammare = (contenuto: PublicContenuto): boolean =>
  contenuto.tipo !== 'post' &&
  (contenuto.stato === 'recorded' ||
    (!!contenuto.videoRegistrato && ['draft', 'text_ready', 'edited'].includes(contenuto.stato)));

/** Girato nel mese di oggi, in base alla data di registrazione. */
const giratoNelMeseCorrente = (contenuto: PublicContenuto): boolean => {
  if (!contenuto.dataRegistrazione) return false;
  const girato = new Date(contenuto.dataRegistrazione);
  const oggi = new Date();
  return girato.getFullYear() === oggi.getFullYear() && girato.getMonth() === oggi.getMonth();
};

/**
 * Scheda "Registrati": reel girati nel mese corrente, in qualunque stato siano
 * oggi (da programmare, programmati, pubblicati, archiviati). Stessa regola del
 * badge "Registrati" della pagina Riprese (reelGiratiNelMese in
 * utils/ripreseMatch.ts): i due numeri devono coincidere.
 */
const èRegistratoNelMese = (contenuto: PublicContenuto): boolean =>
  contenuto.tipo !== 'post' && !!contenuto.videoRegistrato && giratoNelMeseCorrente(contenuto);

/** Reel già in calendario (o montato) ma non ancora segnato come registrato: va girato (02/11/2026). */
const èDaRegistrareInCalendario = (contenuto: PublicContenuto): boolean =>
  contenuto.tipo !== 'post' && !contenuto.videoRegistrato && (contenuto.stato === 'edited' || contenuto.stato === 'scheduled');

// Le reti dove esce un contenuto, con il loro colore (02/11/2026).
const NOMI_RETI: Record<string, string> = { instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok', youtube: 'YouTube' };
const SIGLE_RETI: Record<string, string> = { instagram: 'IG', facebook: 'FB', tiktok: 'TT', youtube: 'YT' };
const COLORI_RETI: Record<string, string> = { instagram: '#C13584', facebook: '#1877F2', tiktok: '#111111', youtube: '#FF0000' };
type UscitaCliente = NonNullable<ContenutoApprovazione['uscita']>;

const RetiUscita: React.FC<{ u: UscitaCliente; piccole?: boolean }> = ({ u, piccole }) => (
  <span className="inline-flex items-center gap-0.5 flex-wrap">
    {u.reti.map((r) => (
      <span
        key={r}
        title={NOMI_RETI[r] ?? r}
        className={`inline-flex items-center rounded font-semibold leading-none ${piccole ? 'px-[3px] py-[1px] text-[8px]' : 'px-1.5 py-0.5 text-[10px] sm:text-[11px]'}`}
        style={{ backgroundColor: COLORI_RETI[r] ?? '#64748b', color: '#ffffff' }}
      >
        {piccole ? SIGLE_RETI[r] ?? r : NOMI_RETI[r] ?? r}
      </span>
    ))}
  </span>
);

const statoUscita = (u: UscitaCliente) =>
  u.stato === 'su_metricool'
    ? { testo: 'Programmato', classi: 'bg-blue-50 text-blue-700 border-blue-200' }
    : u.stato === 'uscito'
      ? { testo: 'Uscito', classi: 'bg-green-50 text-green-700 border-green-200' }
      : { testo: 'In attesa di approvazione', classi: 'bg-amber-50 text-amber-700 border-amber-200' };

const ClientCalendar: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [currentYear] = useState(new Date().getFullYear());
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [cliente, setCliente] = useState<{ id: string; nomeAzienda: string; logoUrl?: string | null; googleDriveLink?: string | null; webAppLeadLink?: string | null; piattaforme: string[]; giorniPreferitiRiprese: string[]; orarioPubblicazione?: string | null; intelAttivo?: boolean } | null>(null);
  const [clientContent, setClientContent] = useState<PublicContenuto[]>([]);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('list');
  const [section, setSection] = useState<'approvare' | 'calendario' | 'drive' | 'competitor' | 'report' | 'lead' | 'rubriche'>('calendario');
  // Reel da approvare (video, copertina e copy da pubblica): se ce ne sono la pagina si apre lì.
  const [approvazioni, setApprovazioni] = useState<ContenutoApprovazione[] | null>(null);
  const [approvazioniErrore, setApprovazioniErrore] = useState<string | null>(null);
  // Rubriche e linee guida del cliente (pagina Copywriter): servono già al primo
  // caricamento, perché il pulsante compare solo se c'è qualcosa da mostrare.
  const [guidelines, setGuidelines] = useState<Guidelines | null>(null);

  // Drive del cliente sfogliato dentro la pagina (come la tab "Drive clip" interna)
  const [driveItems, setDriveItems] = useState<DriveItem[]>([]);
  const [drivePath, setDrivePath] = useState<{ id: string; name: string }[]>([]);
  const [driveMatched, setDriveMatched] = useState<boolean | null>(null);
  const [driveLoading, setDriveLoading] = useState(false);
  const [driveError, setDriveError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<'da_registrare' | 'registrati' | 'programmati' | 'pubblicati'>('da_registrare');
  // Contenuto aperto nella lista "Gestione script": si espande dentro la
  // pagina, non in un popup (qui il cliente scrive e corregge i testi).
  const [contenutoApertoId, setContenutoApertoId] = useState<string | null>(null);
  // Modulo "Nuovo contenuto": il cliente scrive un testo suo, che entra nel
  // conteggio mensile come quelli scritti da noi.
  const [nuovoAperto, setNuovoAperto] = useState(false);

  // Contenuto salvato dalla scheda in linea: aggiorna la lista sul posto,
  // la scheda resta aperta su quel contenuto.
  const handleContentUpdated = useCallback((updatedContent: PublicContenuto) => {
    setClientContent(prev => prev.map(c => c.id === updatedContent.id ? updatedContent : c));
  }, []);

  // Contenuto nuovo scritto dal cliente: va in cima a "Da Registrare".
  const handleContentCreated = useCallback((c: ContenutoCreato) => {
    setClientContent(prev => [{
      id: c.id,
      titolo: c.title,
      descrizione: c.body,
      body: c.body,
      hook: c.hook,
      cta: c.cta,
      note: c.notes,
      stato: c.status,
      dataCreazione: c.created_at,
      dataAggiornamento: c.updated_at,
      tipo: 'reel',
      videoRegistrato: false,
      isPublished: false,
    }, ...prev]);
    setNuovoAperto(false);
    setSelectedTab('da_registrare');
  }, []);

  /**
   * Carica una cartella del Drive del cliente. Senza `folder` parte dalla radice.
   * `depth` valorizzato = click su una briciola del percorso: si torna indietro.
   */
  const caricaDrive = useCallback(
    async (folder?: { id: string; name: string }, depth?: number) => {
      if (!clientId) return;
      setDriveLoading(true);
      setDriveError(null);
      try {
        const qs = folder ? `?folder=${encodeURIComponent(folder.id)}&name=${encodeURIComponent(folder.name)}` : '';
        const res = await fetch(`/api/clients/${clientId}/drive/public${qs}`);
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.message || 'Errore Drive');

        setDriveMatched(!!data.data.matched);
        setDriveItems(data.data.items || []);
        if (!data.data.matched) {
          setDrivePath([]);
        } else if (!folder) {
          setDrivePath([{ id: data.data.folder.id, name: data.data.folder.name }]);
        } else {
          setDrivePath((prev) =>
            typeof depth === 'number' ? prev.slice(0, depth + 1) : [...prev, folder]
          );
        }
      } catch (e: any) {
        setDriveError(e.message || 'Impossibile leggere il Drive');
      } finally {
        setDriveLoading(false);
      }
    },
    [clientId]
  );

  // All'apertura della sezione Drive carica la radice (una volta sola).
  useEffect(() => {
    if (section === 'drive' && driveMatched === null && !driveLoading) caricaDrive();
  }, [section, driveMatched, driveLoading, caricaDrive]);

  // I contenuti da approvare arrivano da pubblica. Se pubblica non risponde la
  // sezione lo dice, il resto della pagina funziona lo stesso.
  useEffect(() => {
    if (!clientId) return;
    let attivo = true;
    fetch(`/api/approvazioni/${clientId}/contenuti`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j: { contenuti: ContenutoApprovazione[] }) => {
        if (!attivo) return;
        setApprovazioni(j.contenuti);
        if (j.contenuti.some((c) => c.si_puo_approvare)) setSection('approvare');
      })
      .catch(() => {
        if (attivo) setApprovazioniErrore('Non riusciamo a caricare i contenuti da approvare in questo momento. Riprova fra qualche minuto.');
      });
    return () => { attivo = false; };
  }, [clientId]);

  // Ricarica in silenzio i contenuti da approvare (dopo un caricamento dell'admin).
  const ricaricaApprovazioni = useCallback(() => {
    if (!clientId) return;
    fetch(`/api/approvazioni/${clientId}/contenuti`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j: { contenuti: ContenutoApprovazione[] }) => {
        setApprovazioni(j.contenuti);
        setApprovazioniErrore(null);
      })
      .catch(() => { /* resta quello che c'era */ });
  }, [clientId]);

  // Admin di social sul link del cliente (02/11/2026): può caricare i reel da qui.
  // Lo decide pubblica chiedendo a social chi è il token del browser; il cliente non ha token.
  // Solo il cookie di login, non il localStorage: cancellati i cookie il riquadro sparisce.
  const [adminLink, setAdminLink] = useState(false);
  useEffect(() => {
    const token = getCookie('bymarke_token');
    if (!clientId || !token) return;
    let attivo = true;
    fetch(`/api/approvazioni/${clientId}/admin`, { cache: 'no-store', headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { admin?: boolean } | null) => { if (attivo && j?.admin) setAdminLink(true); })
      .catch(() => { /* niente pannello */ });
    return () => { attivo = false; };
  }, [clientId]);

  // Gli approvati dal link si vedono nel calendario (29/10/2026: «spostiamo i reel approvati sulla pagina del calendario»).
  const approvatiIds = useMemo(() => new Set((approvazioni ?? []).filter((c) => c.approvato).map((c) => c.id)), [approvazioni]);
  // Quando e dove esce ogni contenuto (02/11/2026, il titolare: «il cliente vede già cosa, quando e su che piattaforme esce»).
  const uscite = useMemo(() => new Map((approvazioni ?? []).filter((c) => c.uscita).map((c) => [c.id, c.uscita!])), [approvazioni]);

  const segnaApprovato = useCallback((id: string) => {
    setApprovazioni((prima) => prima?.map((c) => (c.id === id ? { ...c, approvato: true, si_puo_approvare: false, approvato_il: new Date().toISOString() } : c)) ?? prima);
  }, []);

  // Rubriche e linee guida: si caricano subito perché decidono se il pulsante
  // "Rubriche & Linee Guida" deve comparire. Un errore qui non deve rompere la
  // pagina: al massimo la sezione non viene proposta.
  useEffect(() => {
    if (!clientId) return;
    let attivo = true;
    fetch(`/api/clients/${clientId}/guidelines/public`)
      .then((r) => r.json())
      .then((j) => { if (attivo && j?.success) setGuidelines(j.data); })
      .catch(() => { /* sezione semplicemente non mostrata */ });
    return () => { attivo = false; };
  }, [clientId]);

  // PWA: register service worker + inject dynamic manifest scoped to this client page
  useEffect(() => {
    if (!clientId || !('serviceWorker' in navigator)) return;

    const clientPath = `/calendar/client/${clientId}`;

    // 1. Register service worker
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {});

    // 2. Create dynamic manifest with client-specific start_url
    const manifest = {
      name: cliente ? `${cliente.nomeAzienda} - ByMarke` : 'ByMarke',
      short_name: cliente ? cliente.nomeAzienda : 'ByMarke',
      description: 'Calendario editoriale',
      theme_color: '#fafafb',
      background_color: '#fafafb',
      display: 'standalone',
      start_url: clientPath,
      scope: '/',
      icons: [
        { src: '/android-chrome-192x192.png', sizes: '192x192', type: 'image/png' },
        { src: '/android-chrome-192x192.png', sizes: '192x192', type: 'image/png', purpose: 'maskable' }
      ]
    };

    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
    const manifestUrl = URL.createObjectURL(blob);

    // Remove any existing manifest link
    const existing = document.querySelector('link[rel="manifest"]');
    if (existing) existing.remove();

    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = manifestUrl;
    document.head.appendChild(link);

    return () => {
      link.remove();
      URL.revokeObjectURL(manifestUrl);
    };
  }, [clientId, cliente]);

  // Fetch public calendar data
  useEffect(() => {
    const fetchCalendarData = async () => {
      if (!clientId) return;

      try {
        setIsLoading(true);
        const response = await fetch(`/api/clients/${clientId}/calendar/public`);
        const data = await response.json();

        if (data.success) {
          const mappedClient = {
            id: data.data.client.id,
            nomeAzienda: data.data.client.company_name,
            logoUrl: data.data.client.logo_url || null,
            googleDriveLink: data.data.client.google_drive_link || null,
            webAppLeadLink: data.data.client.web_app_lead_link || null,
            piattaforme: data.data.client.platforms || [],
            giorniPreferitiRiprese: data.data.client.preferred_shooting_days || [],
            orarioPubblicazione: data.data.client.publish_time || null,
            intelAttivo: !!data.data.client.intel_enabled
          };

          interface BackendContent {
            id: string;
            title: string;
            content?: string;
            hook?: string;
            cta?: string;
            status: string;
            videorecorded?: boolean;
            videorecordedat?: string;
            scheduleddate?: string;
            publisheddate?: string;
            createdat?: string;
            updatedat?: string;
            scriptfileurl?: string;
            rawvideourl?: string;
            editedvideourl?: string;
            thumbnailurl?: string;
            publishedlinks?: string;
            analyticsdata?: unknown;
            notes?: string;
            postcopy?: string;
            inspirationlink?: string;
            contenttype?: string;
            postformato?: string | null;
          }

          const mappedContent: PublicContenuto[] = data.data.contents.map((content: BackendContent) => ({
            id: content.id,
            titolo: content.title,
            descrizione: content.content,
            body: content.content,
            hook: content.hook,
            cta: content.cta,
            stato: content.status,
            videoRegistrato: !!content.videorecorded,
            dataRegistrazione: content.videorecordedat,
            dataPubblicazione: content.scheduleddate || content.publisheddate,
            dataCreazione: content.createdat,
            dataAggiornamento: content.updatedat,
            urlScriptFile: content.scriptfileurl,
            urlVideoGrezzo: content.rawvideourl,
            urlVideoMontato: content.editedvideourl,
            urlThumbnail: content.thumbnailurl,
            linkPubblicazione: content.publishedlinks,
            datiAnalitici: content.analyticsdata,
            note: content.notes,
            copyPost: content.postcopy,
            linkIspirazione: content.inspirationlink,
            copertinaUrl: content.thumbnailurl,
            tipo: content.contenttype === 'post' ? 'post' : 'reel',
            formatoPost: content.postformato === 'foto' ? 'foto' : 'grafica',
            isPublished: content.status === 'published' || (content.status === 'archived' && !!content.publisheddate)
          }));

          setCliente(mappedClient);
          setClientContent(mappedContent);
        }
      } catch (error) {
        console.error('Error fetching calendar data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCalendarData();
  }, [clientId]);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const monthNames = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  // Group content by date - ONLY scheduled and published content for calendar
  const contenutiRaggrupati = useMemo(() => {
    return clientContent.reduce((acc, contenuto) => {
      const isCalendarContent =
        contenuto.stato === 'scheduled' ||
        contenuto.stato === 'published' ||
        (contenuto.stato === 'archived' && contenuto.dataPubblicazione);

      if (!isCalendarContent || !contenuto.dataPubblicazione) {
        return acc;
      }

      const pubDate = new Date(contenuto.dataPubblicazione);
      const dataString = createDateKey(pubDate);

      if (!acc[dataString]) {
        acc[dataString] = [];
      }
      acc[dataString].push(contenuto);
      return acc;
    }, {} as Record<string, PublicContenuto[]>);
  }, [clientContent]);

  // Separate content by status
  const contenutiProgrammati = useMemo(() => {
    return clientContent.filter(contenuto => {
      const isCalendarContent =
        contenuto.stato === 'scheduled' ||
        contenuto.stato === 'published' ||
        (contenuto.stato === 'archived' && contenuto.dataPubblicazione);
      return isCalendarContent && contenuto.dataPubblicazione;
    });
  }, [clientContent]);

  const contenutiProgrammatiLista = useMemo(() => {
    return clientContent.filter(contenuto => contenuto.stato === 'scheduled');
  }, [clientContent]);

  /**
   * Da registrare per il cliente = quello che il videomaker ha ancora da girare.
   * Ai testi pronti/bozze si aggiungono i contenuti già montati o programmati
   * ma non ancora confermati come girati: prima sparivano da questa lista
   * appena veniva messa la data, e il cliente non vedeva più che doveva girarli
   * (stessa regola della pagina Videomaker, che invece li contava).
   */
  const contenutiDaRegistrare = useMemo(() => {
    const lista = clientContent.filter(contenuto =>
      ((contenuto.stato === 'text_ready' || contenuto.stato === 'draft') && !èRegistratoDaProgrammare(contenuto)) ||
      // I post grafici non si girano: una volta programmati stanno solo in "Programmati"
      èDaRegistrareInCalendario(contenuto)
    );
    // Prima quelli già in calendario, dal più vicino (02/11/2026, il titolare: «per il cliente è facile
    // capire quali sono da registrare per le prossime sessioni»); poi gli altri nell'ordine di prima.
    const quando = (c: PublicContenuto) => (c.dataPubblicazione ? new Date(c.dataPubblicazione).getTime() : Infinity);
    const inCal = lista.filter(èDaRegistrareInCalendario).sort((a, b) => quando(a) - quando(b));
    return [...inCal, ...lista.filter((c) => !èDaRegistrareInCalendario(c))];
  }, [clientContent]);

  /** Registrati = girati nel mese corrente (èRegistratoNelMese), prima i più recenti. */
  const contenutiRegistrati = useMemo(() => {
    return clientContent
      .filter(èRegistratoNelMese)
      .sort((a, b) => (b.dataRegistrazione || '').localeCompare(a.dataRegistrazione || ''));
  }, [clientContent]);

  const contenutiPubblicati = useMemo(() => {
    return clientContent.filter(contenuto =>
      contenuto.stato === 'published' ||
      (contenuto.stato === 'archived' && contenuto.dataPubblicazione)
    );
  }, [clientContent]);

  const contenutiFiltratiPerTab = useMemo(() => {
    switch (selectedTab) {
      case 'da_registrare':
        return contenutiDaRegistrare;
      case 'registrati':
        return contenutiRegistrati;
      case 'programmati':
        return contenutiProgrammatiLista;
      case 'pubblicati':
        return contenutiPubblicati;
      default:
        return [];
    }
  }, [selectedTab, contenutiDaRegistrare, contenutiRegistrati, contenutiProgrammatiLista, contenutiPubblicati]);

  const tabCounts = useMemo(() => {
    return {
      da_registrare: contenutiDaRegistrare.length,
      registrati: contenutiRegistrati.length,
      programmati: contenutiProgrammatiLista.length,
      pubblicati: contenutiPubblicati.length
    };
  }, [contenutiDaRegistrare, contenutiRegistrati, contenutiProgrammatiLista, contenutiPubblicati]);

  const navigateMonth = (direction: number) => {
    const newMonth = selectedMonth + direction;
    if (newMonth >= 0 && newMonth <= 11) {
      setSelectedMonth(newMonth);
    }
  };

  /**
   * Apre (o chiude) un contenuto nella lista. Dal calendario porta prima sulla
   * scheda giusta, poi scorre fino alla card ed espande: senza popup il
   * contenuto va comunque mostrato dove l'utente può leggerlo e modificarlo.
   */
  const handleContentClick = (contenuto: PublicContenuto) => {
    if (contenutoApertoId === contenuto.id) {
      setContenutoApertoId(null);
      return;
    }
    setSelectedTab(tabDelContenuto(contenuto));
    setContenutoApertoId(contenuto.id);
    // il cambio scheda rimonta la lista: lo scroll va fatto dopo il render
    setTimeout(() => {
      document
        .getElementById(`contenuto-${contenuto.id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  };

  const chiudiContenuto = () => setContenutoApertoId(null);

  /** In quale scheda di "Gestione script" finisce un contenuto. */
  const tabDelContenuto = (contenuto: PublicContenuto): 'da_registrare' | 'registrati' | 'programmati' | 'pubblicati' => {
    const stato = contenuto.stato;
    // Un registrato del mese già programmato o uscito sta anche nella sua scheda:
    // si resta su "Registrati" solo se è lì che lo si sta guardando.
    if (èRegistratoNelMese(contenuto) && (selectedTab === 'registrati' || èRegistratoDaProgrammare(contenuto))) return 'registrati';
    if (èDaRegistrareInCalendario(contenuto)) return 'da_registrare';
    if (stato === 'scheduled') return 'programmati';
    if (stato === 'published' || stato === 'archived') return 'pubblicati';
    return 'da_registrare';
  };

  /**
   * Stato da mostrare al cliente. Quando il videomaker conferma di aver girato
   * un contenuto si accende solo il flag videoRegistrato e lo stato resta
   * 'text_ready' (o 'draft'): senza questo il cliente vedeva "Testo confermato"
   * anche sui contenuti già registrati.
   */
  const statoMostrato = (contenuto: PublicContenuto): string | undefined =>
    contenuto.videoRegistrato && (contenuto.stato === 'text_ready' || contenuto.stato === 'draft')
      ? 'recorded'
      : contenuto.stato;

  // Minimal status styling - monochrome with colored dot indicator
  const getContentStyle = (stato?: string) => {
    switch (stato) {
      case 'draft':
        return { dot: 'bg-slate-500', label: 'Bozza', icon: FileText };
      case 'text_ready':
        return { dot: 'bg-amber-500', label: 'Testo confermato', icon: FileText };
      case 'recorded':
        return { dot: 'bg-blue-500', label: 'Registrato', icon: Video };
      case 'edited':
        return { dot: 'bg-purple-500', label: 'Montato', icon: Sparkles };
      case 'scheduled':
        return { dot: 'bg-orange-500', label: 'Programmato', icon: Clock };
      case 'published':
        return { dot: 'bg-green-500', label: 'Pubblicato', icon: CheckCircle2 };
      case 'archived':
        return { dot: 'bg-slate-400', label: 'Archiviato', icon: CheckCircle2 };
      default:
        return { dot: 'bg-slate-400', label: 'Contenuto', icon: FileText };
    }
  };

  // Raw hex color for inline border-left style (works in both light/dark mode)
  const getContentDotColor = (stato?: string): string => {
    switch (stato) {
      case 'draft': return '#64748b';
      case 'text_ready': return '#f59e0b';
      case 'recorded': return '#3b82f6';
      case 'edited': return '#a855f7';
      case 'scheduled': return '#f97316';
      case 'published': return '#22c55e';
      case 'archived': return '#94a3b8';
      default: return '#94a3b8';
    }
  };

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-4">
            <div className="p-4 bg-slate-100 dark:bg-gray-800 rounded-full w-fit mx-auto animate-spin">
              <Calendar className="h-8 w-8 text-slate-400 dark:text-slate-500" />
            </div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Caricamento calendario...</h1>
            <p className="text-slate-500 dark:text-slate-400">Stiamo caricando i dati del calendario.</p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!cliente) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-4">
            <div className="p-4 bg-slate-100 dark:bg-gray-800 rounded-full w-fit mx-auto">
              <Calendar className="h-8 w-8 text-slate-400 dark:text-slate-500" />
            </div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Cliente non trovato</h1>
            <p className="text-slate-500 dark:text-slate-400">Il cliente richiesto non esiste o non è accessibile.</p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  // Sezione "News & Competitor": c'è se il cliente ha fonti censite
  const mostraCompetitor = !!cliente.intelAttivo;
  // Sezione "Rubriche & Linee Guida": solo per i clienti che hanno almeno una
  // rubrica o un brief creativo compilato nella pagina Copywriter.
  const mostraRubriche = !!guidelines && (guidelines.rubriche.length > 0 || !!guidelines.creative);

  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto py-2 sm:py-4" style={{ WebkitOverflowScrolling: 'touch' }}>
        {/* Header */}
        <div className="flex flex-col gap-4 mb-6 sm:mb-8">
          <div className="flex items-center justify-between sm:justify-start gap-3 sm:gap-4">
            {/* Avatar circolare cliente — logo se presente, altrimenti iniziale */}
            <div
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center"
              style={{
                backgroundColor: '#F4F5F7',
                border: '1px solid #DDE0E6',
                boxShadow: 'none',
              }}
              aria-hidden="true"
            >
              {cliente.logoUrl ? (
                <img
                  src={cliente.logoUrl}
                  alt={`Logo ${cliente.nomeAzienda}`}
                  className="w-full h-full object-cover"
                  draggable={false}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    // fallback all'iniziale se il logo non carica
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <span
                  className="text-lg sm:text-xl font-medium select-none"
                  style={{
                    color: '#13161C',
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontWeight: 600,
                  }}
                >
                  {cliente.nomeAzienda?.charAt(0)?.toUpperCase() || '?'}
                </span>
              )}
            </div>
            <div className="text-left min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-white truncate" style={{ letterSpacing: '-0.02em' }}>
                {cliente.nomeAzienda}
              </h1>
              <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 mt-1">Calendario Editoriale</p>
              {cliente.giorniPreferitiRiprese && cliente.giorniPreferitiRiprese.length > 0 && (
                <>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {/* Su mobile l'etichetta si prende tutta la riga (basis-full):
                        così i giorni e l'orario restano insieme sulla riga sotto,
                        invece di vedersi l'orario spinto a capo da solo.
                        Da sm in su nulla cambia: resta tutto su una riga. */}
                    <span className="basis-full sm:basis-auto text-[10px] sm:text-xs uppercase tracking-[0.18em] font-medium text-slate-400 dark:text-slate-500 sm:mr-1">
                      Giorni pubblicazione
                    </span>
                    {cliente.giorniPreferitiRiprese.map((giorno) => {
                      const dayMap: Record<string, string> = {
                        monday: 'Lun', tuesday: 'Mar', wednesday: 'Mer',
                        thursday: 'Gio', friday: 'Ven', saturday: 'Sab', sunday: 'Dom',
                        'Lunedì': 'Lun', 'Martedì': 'Mar', 'Mercoledì': 'Mer',
                        'Giovedì': 'Gio', 'Venerdì': 'Ven', 'Sabato': 'Sab', 'Domenica': 'Dom'
                      };
                      const label = dayMap[giorno] || giorno.slice(0, 3);
                      return (
                        <span
                          key={giorno}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] sm:text-xs font-medium bg-slate-100 dark:bg-white/[0.06] text-slate-700 dark:text-white/80 border border-slate-200 dark:border-white/10"
                        >
                          {label}
                        </span>
                      );
                    })}
                    {/* Orario concordato: sta accanto ai giorni perché è la
                        stessa informazione — quando escono i contenuti. */}
                    {cliente.orarioPubblicazione && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] sm:text-xs font-medium bg-slate-100 dark:bg-white/[0.06] text-slate-700 dark:text-white/80 border border-slate-200 dark:border-white/10">
                        <Clock className="w-3 h-3 flex-shrink-0" />
                        {cliente.orarioPubblicazione}
                      </span>
                    )}
                  </div>
                  <p className="mt-1.5 text-[10px] sm:text-[11px] leading-snug italic text-slate-400 dark:text-slate-500">
                    Le pubblicazioni possono slittare in caso di mancata conferma del cliente, ma vengono sempre recuperate.
                  </p>
                </>
              )}
            </div>
          </div>

        </div>

        {/* Barra sezioni: c'è per tutti i clienti (qui si aggiungono le voci nuove).
            "Calendario" è sempre presente; le altre compaiono se il cliente ha
            quel servizio attivo. "Drive Clip" porta fuori, sulla cartella Google. */}
        <div className="flex flex-wrap gap-2 mb-6">
          {((approvazioni && approvazioni.length > 0) || adminLink) && (
            <button
              onClick={() => setSection('approvare')}
              title="Video, copertina e testo dei prossimi contenuti, da guardare e approvare"
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium border transition ${
                section === 'approvare'
                  ? 'bg-[#13161C] text-white border-[#13161C]'
                  : 'bg-transparent text-slate-600 border-[#ebedf1] hover:border-[#dde0e6] hover:text-slate-900'
              }`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Da approvare
              {(approvazioni ?? []).filter((c) => c.si_puo_approvare).length > 0 && (
                <span className={`ml-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${section === 'approvare' ? 'bg-white text-[#13161C]' : 'bg-[#13161C] text-white'}`}>
                  {(approvazioni ?? []).filter((c) => c.si_puo_approvare).length}
                </span>
              )}
            </button>
          )}

          <button
            onClick={() => setSection('calendario')}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium border transition ${
              section === 'calendario'
                ? 'bg-[#f0f1f4] text-slate-900 border-[#dde0e6]'
                : 'bg-transparent text-slate-600 border-[#ebedf1] hover:border-[#dde0e6] hover:text-slate-900'
            }`}
          >
            <Calendar className="w-3.5 h-3.5" />
            Calendario
          </button>

          {mostraRubriche && (
            <button
              onClick={() => setSection('rubriche')}
              title="Le rubriche ricorrenti e le linee guida con cui realizziamo i tuoi contenuti"
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium border transition ${
                section === 'rubriche'
                  ? 'bg-[#f0f1f4] text-slate-900 border-[#dde0e6]'
                  : 'bg-transparent text-slate-600 border-[#ebedf1] hover:border-[#dde0e6] hover:text-slate-900'
              }`}
            >
              <BookOpen className="w-3.5 h-3.5" />
              Rubriche & Linee Guida
            </button>
          )}

          {mostraCompetitor && (
            <button
              onClick={() => setSection('competitor')}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium border transition ${
                section === 'competitor'
                  ? 'bg-[#f0f1f4] text-slate-900 border-[#dde0e6]'
                  : 'bg-transparent text-slate-600 border-[#ebedf1] hover:border-[#dde0e6] hover:text-slate-900'
              }`}
            >
              <Newspaper className="w-3.5 h-3.5" />
              News & Competitor
            </button>
          )}

          {cliente.googleDriveLink && (
            <button
              onClick={() => setSection('drive')}
              title="Tutte le clip girate dai videomaker ByMarke: girato grezzo, B-roll e materiali di produzione"
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium border transition ${
                section === 'drive'
                  ? 'bg-[#f0f1f4] text-slate-900 border-[#dde0e6]'
                  : 'bg-transparent text-slate-600 border-[#ebedf1] hover:border-[#dde0e6] hover:text-slate-900'
              }`}
            >
              <FolderOpen className="w-3.5 h-3.5" />
              Drive Clip
            </button>
          )}

          {/* Servizi ByMarke: stavano in fondo al calendario, ora hanno un
              pulsante ciascuno come le altre sezioni. */}
          <button
            onClick={() => setSection('report')}
            title="Report su visibilità, interazioni ed engagement dei tuoi contenuti"
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium border transition ${
              section === 'report'
                ? 'bg-[#f0f1f4] text-slate-900 border-[#dde0e6]'
                : 'bg-transparent text-slate-600 border-[#ebedf1] hover:border-[#dde0e6] hover:text-slate-900'
            }`}
          >
            <BarChart3 className="w-3.5 h-3.5" />
            Report Social
          </button>

          <button
            onClick={() => setSection('lead')}
            title="La web app per ricevere e gestire i lead dal telefono"
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium border transition ${
              section === 'lead'
                ? 'bg-[#f0f1f4] text-slate-900 border-[#dde0e6]'
                : 'bg-transparent text-slate-600 border-[#ebedf1] hover:border-[#dde0e6] hover:text-slate-900'
            }`}
          >
            <Smartphone className="w-3.5 h-3.5" />
            Web Lead App
          </button>
        </div>

        {section === 'approvare' ? (
          <ClientApprovazioni clientId={clientId || ''} contenuti={approvazioni} errore={approvazioniErrore} onApprovato={segnaApprovato} admin={adminLink} onRicarica={ricaricaApprovazioni} />
        ) : section === 'rubriche' && mostraRubriche ? (
          <ClientGuidelines dati={guidelines!} />
        ) : section === 'report' ? (
          <div className="max-w-2xl">
            <CardReportKpi />
          </div>
        ) : section === 'lead' ? (
          <div className="max-w-2xl">
            <CardWebLeadApp link={cliente.webAppLeadLink} />
          </div>
        ) : section === 'competitor' && mostraCompetitor ? (
          <CompetitorIntel clientId={clientId || ''} clientName={cliente.nomeAzienda} />
        ) : section === 'drive' ? (
          /* Drive del cliente sfogliato qui dentro: stessa navigazione della
             tab "Drive clip" interna, senza mandare il cliente su Google. */
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200/60 dark:border-gray-700/60 overflow-hidden">
            <div className="p-3 sm:p-4 md:p-6 border-b border-slate-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white" style={{ letterSpacing: '-0.02em' }}>
                    Drive Clip
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                    Girato grezzo, B-roll e materiali delle riprese
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {driveLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                  {/* Scorciatoia per aprire in Google Drive la cartella in cui
                      ci si trova: serve per scaricare o caricare file. */}
                  {drivePath.length > 0 && (
                    <a
                      href={`https://drive.google.com/drive/folders/${drivePath[drivePath.length - 1].id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-gray-500 hover:text-slate-900 dark:hover:text-white transition-colors whitespace-nowrap"
                    >
                      Apri in Drive
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>

              {/* Percorso: ogni briciola riporta al livello corrispondente */}
              {drivePath.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-1 text-xs sm:text-sm">
                  {drivePath.map((p, i) => (
                    <span key={p.id} className="inline-flex items-center gap-1">
                      {i > 0 && <ChevronRight className="w-3 h-3 text-slate-400 flex-shrink-0" />}
                      <button
                        onClick={() => { if (i < drivePath.length - 1) caricaDrive(p, i); }}
                        disabled={i === drivePath.length - 1 || driveLoading}
                        className={
                          i === drivePath.length - 1
                            ? 'font-medium text-slate-800 dark:text-slate-100'
                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors'
                        }
                      >
                        {p.name}
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="p-3 sm:p-4 md:p-6">
              {driveError ? (
                <p className="py-8 text-center text-sm text-amber-600 dark:text-amber-400">{driveError}</p>
              ) : driveMatched === false ? (
                <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  Nessuna cartella Drive collegata a questo cliente.
                </p>
              ) : driveLoading && driveItems.length === 0 ? (
                <div className="py-10 flex justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : driveItems.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  Questa cartella è vuota.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                  {driveItems.map((item) =>
                    item.isFolder ? (
                      <button
                        key={item.id}
                        onClick={() => caricaDrive({ id: item.id, name: item.name })}
                        disabled={driveLoading}
                        className="flex items-center gap-2.5 p-3 rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-slate-300 dark:hover:border-gray-600 hover:shadow-sm transition-all text-left min-h-[52px]"
                      >
                        <FolderOpen className="w-5 h-5 text-slate-900 flex-shrink-0" />
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{item.name}</span>
                      </button>
                    ) : (
                      <a
                        key={item.id}
                        href={item.webViewLink || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2.5 p-3 rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-slate-300 dark:hover:border-gray-600 hover:shadow-sm transition-all min-h-[52px]"
                      >
                        {item.mimeType.startsWith('video/') ? (
                          <Video className="w-5 h-5 text-blue-400 flex-shrink-0" />
                        ) : item.mimeType.startsWith('image/') ? (
                          <Smartphone className="w-5 h-5 text-purple-400 flex-shrink-0" />
                        ) : (
                          <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                        <span className="text-sm text-slate-700 dark:text-slate-200 truncate flex-1">{item.name}</span>
                        <ExternalLink className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      </a>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (<>
        {/* Vista Lista/Calendario + navigazione mese: appartengono a questa
            sezione, quindi stanno qui dentro e non nell'intestazione del
            profilo, dove comparivano anche nelle altre sezioni. */}
        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          {/* View mode toggle */}
          <div className="flex bg-slate-100 dark:bg-gray-800 rounded-xl p-1 w-full sm:w-auto">
            <button
              onClick={() => setViewMode('list')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-700 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              Lista
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'calendar'
                  ? 'bg-white dark:bg-gray-700 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <span className="hidden sm:inline">Calendario</span>
              <span className="sm:hidden">Cal.</span>
            </button>
          </div>

          {/* Month navigation */}
          {viewMode === 'calendar' && (
            <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto justify-center sm:justify-start">
              <button
                onClick={() => navigateMonth(-1)}
                disabled={selectedMonth === 0}
                className="p-2 sm:p-2.5 bg-slate-100 dark:bg-gray-800 hover:bg-slate-200 dark:hover:bg-gray-700 active:bg-slate-300 dark:active:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-all"
              >
                <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600 dark:text-slate-300" />
              </button>

              <div className="text-sm sm:text-lg font-semibold text-slate-900 dark:text-white min-w-[100px] sm:min-w-[120px] text-center">
                <span className="hidden sm:inline">{monthNames[selectedMonth]}</span>
                <span className="sm:hidden">{monthNames[selectedMonth].substring(0, 3)}</span>
                {' '}{currentYear}
              </div>

              <button
                onClick={() => navigateMonth(1)}
                disabled={selectedMonth === 11}
                className="p-2 sm:p-2.5 bg-slate-100 dark:bg-gray-800 hover:bg-slate-200 dark:hover:bg-gray-700 active:bg-slate-300 dark:active:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-all"
              >
                <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600 dark:text-slate-300" />
              </button>
            </div>
          )}
        </div>

        {/* Calendar or List View */}
        {viewMode === 'calendar' ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200/60 dark:border-gray-700/60 overflow-hidden">
            {/* Calendar header */}
            <div className="p-3 sm:p-4 md:p-6 border-b border-slate-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white" style={{ letterSpacing: '-0.02em' }}>
                  Contenuti Programmati
                </h2>
                <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  {contenutiProgrammati.length} contenuti programmati
                </div>
              </div>
            </div>

            {/* Calendar grid */}
            <div className="p-2 sm:p-4 md:p-6 overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className={`grid gap-1 grid-cols-7 ${isMobile ? 'min-w-[320px]' : ''}`}>
                {/* Day headers */}
                {(isMobile
                  ? ['L', 'M', 'M', 'G', 'V', 'S', 'D']
                  : ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
                ).map((day, index) => (
                  <div key={`day-${index}`} className={`text-center font-semibold text-white bg-slate-900 dark:bg-slate-700 rounded-lg ${
                    isMobile ? 'p-1.5 text-[10px] sm:text-xs' : 'p-2 md:p-3 text-xs md:text-sm'
                  }`}>
                    {day}
                  </div>
                ))}

                {/* Calendar days */}
                {(() => {
                  const calendarDays = generateCalendarDays(currentYear, selectedMonth);

                  return calendarDays.map((dayInfo) => {
                    const contenutiGiorno = contenutiRaggrupati[dayInfo.dayString] || [];

                    let backgroundClass = '';
                    let borderClass = '';

                    if (dayInfo.isToday) {
                      backgroundClass = 'bg-slate-50 dark:bg-gray-700';
                      borderClass = 'border-slate-300 dark:border-slate-500 ring-1 sm:ring-2 ring-slate-300 dark:ring-slate-500';
                    } else if (dayInfo.isCurrentMonth) {
                      backgroundClass = 'bg-white dark:bg-gray-800';
                      borderClass = 'border-slate-200 dark:border-gray-700';
                    } else {
                      backgroundClass = 'bg-slate-50/50 dark:bg-gray-800/50';
                      borderClass = 'border-slate-100 dark:border-gray-700/50';
                    }

                    return (
                      <div
                        key={dayInfo.dayString}
                        className={`border rounded-lg transition-all duration-200 ${
                          isMobile
                            ? 'min-h-[60px] sm:min-h-[80px] p-1 sm:p-2'
                            : 'min-h-[100px] md:min-h-[120px] p-2 md:p-3'
                        } ${backgroundClass} ${borderClass}`}
                      >
                        {/* Day number */}
                        <div className={`text-center ${isMobile ? 'mb-0.5 sm:mb-1' : 'mb-1 md:mb-2'}`}>
                          <div className={`font-semibold ${
                            isMobile ? 'text-[10px] sm:text-xs' : 'text-xs md:text-sm'
                          } ${
                            dayInfo.isToday
                              ? 'text-slate-900 dark:text-white font-bold'
                              : dayInfo.isCurrentMonth
                                ? 'text-slate-900 dark:text-slate-100'
                                : 'text-slate-400 dark:text-slate-600'
                          }`}>
                            {dayInfo.displayDay}
                          </div>
                        </div>

                        {/* Content items */}
                        <div className={`space-y-0.5 ${isMobile ? '' : 'sm:space-y-1'}`}>
                          {contenutiGiorno.slice(0, isMobile ? 1 : 3).map((contenuto) => (
                            <div
                              key={contenuto.id}
                              onClick={() => handleContentClick(contenuto)}
                              className={`rounded cursor-pointer transition-all duration-200 border-l-2 border border-slate-200 dark:border-gray-600 bg-slate-50 dark:bg-gray-700/50 hover:bg-slate-100 dark:hover:bg-gray-700 active:scale-95 ${
                                isMobile ? 'text-[8px] sm:text-[10px] p-0.5 sm:p-1' : 'text-[10px] md:text-xs p-1 md:p-2'
                              }`}
                              style={{ borderLeftColor: getContentDotColor(statoMostrato(contenuto)) }}
                              title={`${contenuto.titolo} - ${isMobile ? 'Tocca' : 'Clicca'} per dettagli`}
                            >
                              {/* Anteprima del reel/post nel calendario (03/11/2026, il titolare: «si devono vedere anche lì le anteprime»). */}
                              {contenuto.copertinaUrl && (
                                <img src={contenuto.copertinaUrl} alt="" loading="lazy" className={`w-full ${contenuto.tipo === 'post' ? 'aspect-[4/5]' : 'aspect-[9/16]'} object-cover rounded ${isMobile ? 'mb-0.5' : 'mb-1'} border border-slate-200 dark:border-gray-600`} />
                              )}
                              <div className="font-medium text-slate-700 dark:text-slate-300 truncate leading-tight">
                                {approvatiIds.has(contenuto.id) && <span className="text-green-700 dark:text-green-400 font-bold" title="Approvato">✓ </span>}
                                {isMobile
                                  ? contenuto.titolo.substring(0, 8) + (contenuto.titolo.length > 8 ? '..' : '')
                                  : contenuto.titolo.substring(0, 20) + (contenuto.titolo.length > 20 ? '...' : '')
                                }
                              </div>
                              {!isMobile && contenuto.dataPubblicazione && (
                                <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                                  {format(new Date(contenuto.dataPubblicazione), 'HH:mm')}
                                </div>
                              )}
                              {uscite.get(contenuto.id) && uscite.get(contenuto.id)!.reti.length > 0 && (
                                <div className="mt-0.5" title={`${statoUscita(uscite.get(contenuto.id)!).testo}: ${uscite.get(contenuto.id)!.reti.map((r) => NOMI_RETI[r] ?? r).join(', ')}`}>
                                  <RetiUscita u={uscite.get(contenuto.id)!} piccole />
                                </div>
                              )}
                            </div>
                          ))}

                          {contenutiGiorno.length > (isMobile ? 1 : 3) && (
                            <div className={`text-slate-400 dark:text-slate-500 text-center ${isMobile ? 'text-[8px] py-0.5' : 'text-[10px] py-1'}`}>
                              +{contenutiGiorno.length - (isMobile ? 1 : 3)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        ) : (
          /* List View */
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200/60 dark:border-gray-700/60 overflow-hidden">
            <div className="p-3 sm:p-4 md:p-6 border-b border-slate-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-4">
                <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white" style={{ letterSpacing: '-0.02em' }}>
                  Gestione script
                </h2>
                <div className="flex items-center justify-between sm:justify-end gap-3">
                  <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                    {contenutiFiltratiPerTab.length} contenuti
                  </div>
                  {!nuovoAperto && (
                    <button
                      onClick={() => { setNuovoAperto(true); setContenutoApertoId(null); }}
                      className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-slate-800 bg-gradient-to-b from-[#fff7a8] to-[#fdf751] hover:from-[#fdf751] hover:to-[#f5ef00] rounded-xl transition-all flex items-center gap-1.5 shadow-sm min-h-[40px]"
                    >
                      <Plus className="w-4 h-4" />
                      Nuovo contenuto
                    </button>
                  )}
                </div>
              </div>

              {nuovoAperto && (
                <div className="mb-4 p-3 sm:p-4 rounded-xl border border-[#fdf751] dark:border-[#fdf751]/60 ring-1 ring-[#fdf751]/30">
                  <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-white mb-2" style={{ letterSpacing: '-0.02em' }}>
                    Nuovo contenuto
                  </h3>
                  <ClientNewContentForm
                    clientId={clientId || ''}
                    onCreated={handleContentCreated}
                    onClose={() => setNuovoAperto(false)}
                  />
                </div>
              )}

              {/* Tabs */}
              <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="flex space-x-1 bg-slate-100 dark:bg-gray-700 rounded-xl p-1 min-w-min">
                  {([
                    { key: 'da_registrare' as const, label: 'Da Registrare' },
                    { key: 'registrati' as const, label: 'Registrati' },
                    { key: 'programmati' as const, label: 'Programmati' },
                    // La scheda raccoglie sia i pubblicati sia gli archiviati con
                    // data di pubblicazione: il nome lo dice esplicitamente.
                    { key: 'pubblicati' as const, label: 'Pubblicati o Archiviati' },
                  ]).map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setSelectedTab(tab.key)}
                      className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                        selectedTab === tab.key
                          ? 'bg-white dark:bg-gray-600 text-slate-900 dark:text-white shadow-sm'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white active:bg-slate-200 dark:active:bg-gray-600'
                      }`}
                    >
                      {tab.label} ({tabCounts[tab.key]})
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-3 sm:p-4 md:p-6 space-y-2 sm:space-y-3">
              {contenutiFiltratiPerTab.length > 0 ? (
                contenutiFiltratiPerTab.map((contenuto) => {
                  // Nella scheda "Registrati" il tag è sempre "Registrato", anche se il
                  // reel nel frattempo è stato programmato o archiviato.
                  const statoCard = selectedTab === 'registrati' ? 'recorded' : statoMostrato(contenuto);
                  const daGirareInCal = selectedTab === 'da_registrare' && èDaRegistrareInCalendario(contenuto);
                  const contentStyle = daGirareInCal
                    ? {
                        ...getContentStyle(statoCard),
                        dot: 'bg-red-500',
                        label: contenuto.dataPubblicazione
                          ? `Da registrare · esce il ${format(new Date(contenuto.dataPubblicazione), 'EEE d MMM', { locale: it })}`
                          : 'Da registrare',
                      }
                    : getContentStyle(statoCard);
                  // Nei registrati conta quando è stato girato, non la pubblicazione
                  const dataGirato = selectedTab === 'registrati' ? contenuto.dataRegistrazione : undefined;
                  const dateToShow = dataGirato || contenuto.dataPubblicazione;

                  const aperto = contenutoApertoId === contenuto.id;

                  return (
                    <div
                      key={contenuto.id}
                      id={`contenuto-${contenuto.id}`}
                      className={`p-3 sm:p-4 rounded-xl border bg-white dark:bg-gray-800 transition-all duration-200 ${
                        aperto
                          ? 'border-[#fdf751] dark:border-[#fdf751]/60 ring-1 ring-[#fdf751]/30 shadow-sm scroll-mt-4'
                          : 'border-slate-200 dark:border-gray-700 hover:border-slate-300 dark:hover:border-gray-600 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        {/* Intestazione: apre e chiude la scheda */}
                        <div className="cursor-pointer" onClick={() => handleContentClick(contenuto)}>
                        {/* Status row */}
                        <div className={`flex flex-wrap items-center gap-2 mb-2 ${aperto ? 'hidden' : ''}`}>
                          <span className="inline-flex items-center gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-gray-600">
                            <span className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full flex-shrink-0 ${contentStyle.dot}`}></span>
                            {contentStyle.label}
                          </span>
                          {approvatiIds.has(contenuto.id) && (
                            <span className="inline-flex items-center gap-1 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800">
                              <CheckCircle2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Approvato
                            </span>
                          )}
                          {dateToShow && (
                            <span className="text-[10px] sm:text-sm text-slate-400 dark:text-slate-500">
                              {format(new Date(dateToShow), isMobile ? 'dd/MM/yy' : 'dd MMM yyyy', { locale: it })}
                            </span>
                          )}
                        </div>

                        {/* Quando e dove esce (02/11/2026) */}
                        {uscite.get(contenuto.id) && (
                          <div className="flex flex-wrap items-center gap-2 mb-2 text-[11px] sm:text-xs text-slate-600 dark:text-slate-300">
                            {/* Un reel ancora da girare non ha niente da approvare: lì basta quando e dove esce. */}
                            {!daGirareInCal && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full border font-semibold ${statoUscita(uscite.get(contenuto.id)!).classi}`}>
                                {statoUscita(uscite.get(contenuto.id)!).testo}
                              </span>
                            )}
                            <span className="font-medium">
                              {format(new Date(`${uscite.get(contenuto.id)!.data_roma}T12:00:00`), 'EEE d MMM', { locale: it })} · {uscite.get(contenuto.id)!.ora_roma}
                            </span>
                            <RetiUscita u={uscite.get(contenuto.id)!} />
                          </div>
                        )}

                        {/* Title */}
                        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-slate-900 dark:text-white mb-2 sm:mb-3 line-clamp-2" style={{ letterSpacing: '-0.02em' }}>
                          {contenuto.titolo}
                        </h3>

                        {/* Anteprima: solo a scheda chiusa */}
                        {!aperto && contenuto.tipo === 'post' && (
                        <div className="flex gap-3 bg-slate-50 dark:bg-gray-700/50 rounded-xl p-2 sm:p-3 md:p-4 border border-slate-100 dark:border-gray-600">
                          {contenuto.copertinaUrl && (
                            <img src={contenuto.copertinaUrl} alt="" className="w-14 h-[70px] sm:w-16 sm:h-20 rounded-lg object-cover flex-shrink-0 border border-slate-200 dark:border-gray-600" />
                          )}
                          <div className="min-w-0">
                            <span className="text-[10px] sm:text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">{contenuto.formatoPost === 'foto' ? 'Carosello foto' : 'Post grafico'} · Copy</span>
                            <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed mt-0.5 line-clamp-3">
                              {contenuto.copyPost || 'Copy da scrivere'}
                            </p>
                          </div>
                        </div>
                        )}
                        {!aperto && contenuto.tipo !== 'post' && (
                        <div className="space-y-2 sm:space-y-3 bg-slate-50 dark:bg-gray-700/50 rounded-xl p-2 sm:p-3 md:p-4 border border-slate-100 dark:border-gray-600 flow-root">
                          {contenuto.copertinaUrl && (
                            <img src={contenuto.copertinaUrl} alt="" loading="lazy" className="float-left mr-3 w-16 h-[114px] sm:w-20 sm:h-[142px] rounded-lg object-cover border border-slate-200 dark:border-gray-600" />
                          )}
                          {contenuto.hook && (
                            <div>
                              <span className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Hook</span>
                              <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed mt-0.5 line-clamp-2">
                                {contenuto.hook}
                              </p>
                            </div>
                          )}

                          {(contenuto.descrizione || contenuto.body) && (
                            <div>
                              <span className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Corpo</span>
                              <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed mt-0.5 line-clamp-2 sm:line-clamp-3">
                                {contenuto.descrizione || contenuto.body}
                              </p>
                            </div>
                          )}

                          {contenuto.cta && (
                            <div>
                              <span className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">CTA</span>
                              <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 font-medium mt-0.5 line-clamp-1">
                                {contenuto.cta}
                              </p>
                            </div>
                          )}
                        </div>
                        )}

                        {/* Invito ad aprire: solo a scheda chiusa */}
                        {!aperto && (
                          <div className="mt-2 sm:mt-3 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium">
                            <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            <span>{isMobile ? 'Tocca per aprire e modificare' : 'Clicca per aprire e modificare'}</span>
                          </div>
                        )}
                        </div>

                        {/* Scheda completa in linea: qui si legge e si scrive,
                            senza popup e senza uscire dalla pagina. */}
                        {aperto && (
                          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-200 dark:border-gray-700">
                            <ClientContentEditor
                              content={contenuto}
                              clientId={clientId || ''}
                              onContentUpdated={handleContentUpdated}
                              getContentStyle={() => getContentStyle(statoCard)}
                              onClose={chiudiContenuto}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-8 sm:p-12 text-center">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-slate-400 dark:text-slate-500" />
                  </div>
                  <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 font-medium">
                    {selectedTab === 'da_registrare' && 'Nessun contenuto da registrare'}
                    {selectedTab === 'registrati' && 'Nessun reel registrato questo mese'}
                    {selectedTab === 'programmati' && 'Nessun contenuto programmato'}
                    {selectedTab === 'pubblicati' && 'Nessun contenuto pubblicato o archiviato'}
                  </p>
                  <p className="text-xs sm:text-sm text-slate-400 dark:text-slate-500 mt-1">
                    I contenuti appariranno qui quando disponibili
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        </>)}

      </div>
    </PublicLayout>
  );
};

export default ClientCalendar;
